import React, { useEffect, useMemo, useState } from 'react';
import { Btn, Chip, DataTable, KPI, KPIRow, Loading, Msg, Section, Select, useAdminGet } from '../../kit';
import { PeriodPicker, type Period, periodText, usePeriodParams } from '../../period';
import { AdminLink } from '../../adminNav';
import { fmtN } from '../../monthly';
import { Check } from '../emailKit';
import { fmtIST } from '../trace/DeliveryTrace';
import { ATTR_LABELS, FunnelSteps } from '../campaigns/shared';
import {
  AutomatedToggle, EngagementNote, N, Pct, SECTION, WINDOW_DAYS, WINDOW_LABELS, arr, dayText, num, pctText, rangeOf,
  rateOf, shareHint, str,
} from '../dashboard/shared';
import InviteRunDrawer from './InviteRunDrawer';
import {
  GroupNote, GroupSelect, RunKind, STEP_WORDS, signinRate, toSteps, useEmailGroups,
} from './shared';

/**
 * Email Center → Invites (owner, 2026-10-15): how the invitation e-mails did —
 * the ones Users → Bulk add sends when its box is ticked, and the ones Users →
 * Send invites sends to an import later. Per run: sent, delivered, opened,
 * clicked, bounced, and how many of those people then signed in.
 *
 * The filter bar narrows everything to a period (India time), to one bulk
 * group (a bulk add, an Account-origin batch or a People import — the
 * invites to its members only), and leaves the team's own addresses out
 * unless asked. A run opens its drawer: funnel, links, every recipient (with
 * a group picked, the run's invites to that group only, as its row counts).
 * Backend: Backend/admin_email_invites.py (email/invite_runs,
 * email/invite_run_detail) and Backend/bulk_groups.py (email/groups).
 *
 * EmailPanel keeps this mounted once opened, so the filters survive a look at
 * another tab; deep links: tab=invites&run=<run id> opens that run,
 * &bulk=<group id> narrows to that group.
 *
 * Opened / clicked are people's (Backend/email_engagement.py): instant reads,
 * Apple MPP and scanner opens and bot clicks are counted apart, per run and in
 * the drawer's recipient tags; "Include automated" shows the raw numbers.
 */

/** A deep link's state (EmailPanel: tab=invites&run=…&bulk=…). */
export type InvitesPreset = { run?: string; bulk?: string; nonce: string };

// Invites are occasional, so the presets reach further back than the
// dashboard's; the default covers the last three months.
const PERIODS = ['30d', '90d', '365d', 'custom'];
const PERIOD_LABELS: Record<string, string> = { ...WINDOW_LABELS, '365d': 'Last 365 days' };
const PERIOD_DAYS: Record<string, number | null> = { ...WINDOW_DAYS, '365d': 365 };
// email/invite_runs takes attr_days 1–90 (no "any time after").
const ATTR_OPTIONS = ['7', '30', '90'];

const IST_MS = 330 * 60 * 1000;
const istDay = (v: any): string => {
  const t = Date.parse(str(v));
  return Number.isNaN(t) ? '' : new Date(t + IST_MS).toISOString().slice(0, 10);
};

const InvitesPanel: React.FC<{ preset?: InvitesPreset | null }> = ({ preset }) => {
  const [period, setPeriod] = useState<Period>({ preset: '90d', from: '', to: '' });
  const { params: pp } = usePeriodParams(period);
  const ppKey = JSON.stringify(pp);
  // rangeOf reads today's date: worked out once per period choice.
  const range = useMemo(() => rangeOf(pp), [ppKey]);   // eslint-disable-line react-hooks/exhaustive-deps
  const [group, setGroup] = useState('');
  const [withTeam, setWithTeam] = useState(false);
  const [attr, setAttr] = useState('30');
  const [automated, setAutomated] = useState(false);
  const [stamp, setStamp] = useState(0);
  // Refresh asks the server to rebuild (refresh=1 drops its 90-second cache);
  // a filter change after that reads the cache again.
  const [fresh, setFresh] = useState(false);
  // The run in the drawer, with its row when it was opened from the table and
  // the group it is narrowed to: the filter's for a row, a deep link's own
  // bulk= (none: the whole run) for a link.
  const [open, setOpen] = useState<{ id: string; row?: any; group?: string } | null>(null);
  const groups = useEmailGroups();

  useEffect(() => {
    if (!preset) return;
    if (preset.bulk !== undefined) setGroup(preset.bulk);
    if (preset.run) setOpen({ id: preset.run, group: preset.bulk || undefined });
  }, [preset?.nonce]);   // eslint-disable-line react-hooks/exhaustive-deps

  const params = useMemo(() => {
    const f: Record<string, string> = { exclude_team: withTeam ? '0' : '1', attr_days: attr };
    if (range.from) f.from = range.from;
    if (range.to) f.to = range.to;
    if (group) f.group = group;
    if (automated) f.automated = '1';
    return f;
  }, [range, withTeam, attr, group, automated]);
  const fKey = JSON.stringify(params);
  const reads = fresh ? { ...params, refresh: '1' } : params;
  const q = useAdminGet(SECTION, 'invite_runs', reads, [fKey, stamp]);
  useEffect(() => { if (fresh && !q.loading) setFresh(false); }, [fresh, q.loading]);
  const d: any = q.data || {};
  const k: any = d.kpis || {};
  const steps = toSteps(d.steps, k);
  const echo: any = d.filters?.group && typeof d.filters.group === 'object' ? d.filters.group : null;
  const g = groups.byId(group);
  const label = periodText({ preset: pp.window || '90d', from: pp.from || '', to: pp.to || '' }, PERIOD_LABELS);
  const shownFrom = str(d.period?.from) || range.from || '';
  const shownTo = str(d.period?.to) || range.to || '';
  const nothingDelivered = 'delivered' in k && !num(k.delivered);
  const sr = signinRate(k);

  const rows = useMemo(() => arr(d.runs).map((r: any) => {
    const grp = r?.group && typeof r.group === 'object' && str(r.group.id) ? r.group : null;
    return {
      ...r,
      run_id: str(r?.run_id),
      run: str(r?.label) || (str(r?.run_id) === 'single' ? 'One-off invites' : str(r?.run_id)),
      started_at: r?.at ?? null,
      by: str(r?.by),
      group_id: grp ? str(grp.id) : '',
      group_label: grp ? str(grp.label) || str(grp.id) : '',
      open_rate: rateOf(r, 'open_rate'),
      click_rate: rateOf(r, 'click_rate'),
      ctor: rateOf(r, 'ctor'),
      signin_rate: signinRate(r),
    };
  }).filter((r: any) => r.run_id), [d.runs]);

  // The drawer reads the same days as the row (the whole run for a dated one,
  // the period for the pooled one-off invites); a deep link sends none.
  const drawerPeriod = (row?: any): Record<string, string> | undefined => {
    if (!row) return undefined;
    const from = row.run_id === 'single' ? range.from : istDay(row.started_at) || range.from;
    const out: Record<string, string> = {};
    if (from) out.from = from;
    if (row.run_id === 'single' && range.to) out.to = range.to;
    return Object.keys(out).length ? out : undefined;
  };

  const chips: { label: string; value: string; clear: () => void }[] = [];
  if (group) chips.push({ label: 'Group', value: g ? g.label : (str(echo?.label) || group), clear: () => setGroup('') });
  if (withTeam) chips.push({ label: 'Team', value: 'included', clear: () => setWithTeam(false) });
  if (automated) chips.push({ label: 'Opens and clicks', value: 'automated included', clear: () => setAutomated(false) });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="text-sm text-grayscale-1200 max-w-2xl">
          Invitation e-mails sent from Users → Bulk add and Users → Send invites. Opens and clicks come from
          SendGrid; 'Signed in' means the person signed in after the invite.
          {shownFrom && <span className="text-grayscale-1100"> Showing {dayText(shownFrom)} – {dayText(shownTo) || 'today'} (India time).</span>}
        </div>
        <PeriodPicker value={period} onChange={setPeriod} options={PERIODS} labels={PERIOD_LABELS}
          presetDays={PERIOD_DAYS} hint="Both days included (India time). Leave one blank for open-ended." />
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <GroupSelect value={group} onChange={setGroup} groups={groups.groups} all="All invites" />
        <Check checked={withTeam} onChange={setWithTeam}>Count the team's own addresses</Check>
        <AutomatedToggle value={automated} onChange={setAutomated} />
        <span className="text-xs text-grayscale-1100">Signed in</span>
        <Select value={attr} onChange={setAttr} options={ATTR_OPTIONS} labels={ATTR_LABELS} />
        <span className="flex-1" />
        <Btn small onClick={() => { groups.reload(); setFresh(true); setStamp(s => s + 1); }}
          title="Count again from the database (otherwise results are kept for 90 seconds)">Refresh</Btn>
      </div>
      {chips.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap -mt-2">
          {chips.map(c => <Chip key={c.label} label={c.label} value={c.value} onClear={c.clear} />)}
          <button type="button" className="text-xs text-grayscale-1100 underline underline-offset-2"
            onClick={() => { setGroup(''); setWithTeam(false); setAutomated(false); }}>clear all</button>
        </div>
      )}
      {group && (
        <GroupNote group={g} id={group} fallbackLabel={str(echo?.label)} people={echo?.people} what="invites to">
          {' '}<AdminLink to={{ group: 'email', tab: 'dashboard', bulk: group }}
            title="The Email Center dashboard narrowed to this group: every e-mail they were sent, not just the invite">
            Every e-mail to this group on the Dashboard →</AdminLink>
        </GroupNote>
      )}
      {groups.error && !groups.groups.length && (
        <div className="text-[11px] text-grayscale-1100">The bulk groups could not be loaded ({groups.error}).</div>
      )}
      {q.error && <Msg kind="error">{q.error}</Msg>}

      {q.loading && !q.data ? <Loading label="Counting invites…" /> : q.data && (
        <>
          <KPIRow>
            <KPI label="Invites sent" value={<N v={k.sent} />}
              hint={num(k.failed) ? `${fmtN(num(k.failed))} could not be sent` : label} />
            <KPI label="Delivered" value={<N v={k.delivered} />}
              hint={num(k.sent) ? `${pctText(num(k.delivered) / num(k.sent))} of sent` : undefined} />
            <KPI label={automated ? 'Open rate (all)' : 'Open rate'} value={nothingDelivered ? '—' : <Pct v={rateOf(k, 'open_rate')} />}
              hint={nothingDelivered ? 'nothing delivered' : `${fmtN(num(k.opened))} opened${automated ? ''
                : k.raw_open_rate !== undefined && k.raw_open_rate !== null ? ` by people · ${pctText(Number(k.raw_open_rate))} with automated` : ''}`} />
            <KPI label={automated ? 'Click rate (all)' : 'Click rate'} value={nothingDelivered ? '—' : <Pct v={rateOf(k, 'click_rate')} />}
              hint={`${fmtN(num(k.clicked))} clicked${automated ? ''
                : k.raw_click_rate !== undefined && k.raw_click_rate !== null ? ` by people · ${pctText(Number(k.raw_click_rate))} with bots` : ''}`} />
            <KPI label="CTOR" value={nothingDelivered ? '—' : <Pct v={rateOf(k, 'ctor')} />} hint="clicks ÷ opens" />
            <KPI label="Bounced" value={<N v={k.bounced} />}
              hint={rateOf(k, 'bounce_rate') !== undefined ? `${pctText(rateOf(k, 'bounce_rate'))} of sent` : undefined} />
            <KPI label="Signed in" value={<N v={k.signed_in} />}
              hint={sr !== undefined ? `${pctText(sr)} of delivered` : `within ${num(attr)} days of the invite`} />
            {'auto_opened' in k && (
              <KPI label="Instant / auto opens" value={<N v={k.auto_opened} />}
                hint={shareHint(k.auto_open_share, 'opened invites had only automated opens') || 'opened only by machines'} />
            )}
            {'bot_clicked' in k && (
              <KPI label="Bot clicks" value={<N v={k.bot_clicked} />}
                hint={shareHint(k.bot_click_share, 'clicked invites had only bot clicks') || 'clicked only by link scanners'} />
            )}
          </KPIRow>
          <EngagementNote eng={d.engagement} automated={automated} />

          <Section title="Funnel"
            right={<span className="text-xs text-grayscale-1100">signed in = first sign-in {ATTR_LABELS[attr]} of the invite</span>}>
            <FunnelSteps steps={steps} words={STEP_WORDS} />
          </Section>

          <Section title={`Runs (${label})`}
            right={<span className="text-xs text-grayscale-1100">{fmtN(num(k.runs ?? rows.length))} run{num(k.runs ?? rows.length) === 1 ? '' : 's'}</span>}>
            <DataTable rows={rows} csvName="invite-runs" tableId="email_invite_runs" defaultSort="-started_at"
              maxHeight="36rem"
              columns={['run', 'started_at', 'by', 'group_label', 'sent', 'delivered', 'open_rate', 'click_rate',
                'auto_opened', 'bot_clicked', 'bounced', 'failed', 'signed_in']}
              optionalColumns={['last_at', 'opened', 'clicked', 'ctor', 'unsubscribed', 'signin_rate', 'run_id']}
              labels={{ run: 'Run', started_at: 'When (IST)', by: 'By', group_label: 'Group', open_rate: 'Open rate',
                click_rate: 'Click rate', signed_in: 'Signed in', last_at: 'Last send (IST)', ctor: 'CTOR',
                signin_rate: 'Sign-in rate', run_id: 'Run id', auto_opened: 'Auto opens', bot_clicked: 'Bot clicks' }}
              render={{
                run: r => (
                  <span className="inline-flex items-center gap-1.5">
                    <span className="font-medium" title={r.run_id}>{r.run}</span><RunKind kind={r.kind} />
                  </span>
                ),
                started_at: r => <span className="whitespace-nowrap">{fmtIST(r.started_at)}</span>,
                last_at: r => <span className="whitespace-nowrap">{fmtIST(r.last_at)}</span>,
                by: r => <span>{r.by || '—'}</span>,
                group_label: r => (r.group_id
                  ? <button type="button" onClick={() => setGroup(x => (x === r.group_id ? '' : r.group_id))}
                      title={group === r.group_id ? 'Filtering by this group — click to clear' : 'Show only invites to this group'}
                      className="text-left hover:underline underline-offset-2 max-w-[16rem] truncate">{r.group_label}</button>
                  : <span className="text-grayscale-1100">—</span>),
                sent: r => <N v={r.sent} />,
                delivered: r => <N v={r.delivered} />,
                opened: r => <N v={r.opened} />,
                clicked: r => <N v={r.clicked} />,
                bounced: r => <N v={r.bounced} />,
                auto_opened: r => <N v={r.auto_opened} />,
                bot_clicked: r => <N v={r.bot_clicked} />,
                unsubscribed: r => <N v={r.unsubscribed} />,
                failed: r => <span className={num(r.failed) > 0 ? 'text-red-700 font-medium' : ''}><N v={r.failed} /></span>,
                open_rate: r => <Pct v={r.open_rate} />,
                click_rate: r => <Pct v={r.click_rate} />,
                ctor: r => <Pct v={r.ctor} />,
                signed_in: r => (
                  <span className="whitespace-nowrap">
                    <N v={r.signed_in} />
                    {r.signin_rate !== undefined && <span className="text-[11px] text-grayscale-1100"> ({pctText(r.signin_rate)})</span>}
                  </span>
                ),
                signin_rate: r => <span>{pctText(r.signin_rate)}</span>,
              }}
              actions={r => <Btn small onClick={() => setOpen({ id: r.run_id, row: r, group: group || undefined })}>Open</Btn>}
              empty={group ? 'No invite to this group in this period.' : 'No invitation e-mail was sent in this period.'} />
          </Section>

          <div className="text-[11px] text-grayscale-1100 flex flex-col gap-1">
            {arr(d.notes).map((n: any, i: number) => <span key={i}>{str(n)}</span>)}
          </div>
        </>
      )}

      {open && (
        <InviteRunDrawer runId={open.id} row={open.row} period={drawerPeriod(open.row)}
          groupId={open.group} groupInfo={open.group ? groups.byId(open.group) : undefined}
          excludeTeam={withTeam ? '0' : '1'} attr={attr} automated={automated} onClose={() => setOpen(null)} />
      )}
    </div>
  );
};

export default InvitesPanel;
